import imageCompression from 'browser-image-compression';
import { supabase } from './supabaseClient';
import { getNegocioId } from './negocioActual';

const BUCKET = 'productos-fotos';

// Las fotos de celular pesan 3-10 MB; comprimirlas antes de subir
// reduce el guardado de >1 min a segundos en conexiones lentas.
// Calidad "equilibrada": nítidas para el catálogo Y para publicar en redes
// (IG/FB), sin volver el catálogo demasiado pesado en internet lento.
const LADO_MAX = 1440;
const CALIDAD = 0.85;
const MAX_MB = 1.2;
// Por encima de esto la foto se considera "sin comprimir" y se intenta otra vía.
const TOPE_BYTES = 1.5 * 1024 * 1024;

const OPCIONES = { maxSizeMB: MAX_MB, maxWidthOrHeight: LADO_MAX, initialQuality: CALIDAD };

// Plan B sin librería: el navegador decodifica la foto en un <img> y la dibuja
// directo a un canvas de 1440px. Sirve cuando la librería falla con fotos muy
// grandes de iPhone (límite de memoria/canvas de Safari): desde ago-2026 cientos
// de fotos de 5-10 MB se subieron así, sin comprimir.
async function redimensionarConCanvas(archivo, forzarJpg = false) {
  const url = URL.createObjectURL(archivo);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
    const escala = Math.min(1, LADO_MAX / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * escala);
    canvas.height = Math.round(img.naturalHeight * escala);
    const ctx = canvas.getContext('2d');
    const esPng = archivo.type === 'image/png' && !forzarJpg;
    if (!esPng) {
      ctx.fillStyle = '#fff'; // JPG no tiene transparencia: fondo blanco, no negro
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const tipo = esPng ? 'image/png' : 'image/jpeg';
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, tipo, CALIDAD));
    if (!blob) throw new Error('canvas vacío');
    const nombre = (archivo.name || 'foto').replace(/\.[^.]+$/, '') + (esPng ? '.png' : '.jpg');
    return new File([blob], nombre, { type: tipo });
  } finally {
    URL.revokeObjectURL(url);
  }
}

const esChica = (f) => f && f.size <= TOPE_BYTES;

// Intenta, en orden: librería con web worker → librería sin worker → canvas.
// Solo si todo falla se sube el original (mejor guardar el producto que perderlo).
export async function comprimirFoto(archivo) {
  if (!archivo?.type?.startsWith('image/')) return archivo;
  const intentos = [
    () => imageCompression(archivo, { ...OPCIONES, useWebWorker: true }),
    () => imageCompression(archivo, { ...OPCIONES, useWebWorker: false }),
    () => redimensionarConCanvas(archivo),
    // PNG grande (p. ej. diseños de catálogo exportados): como JPG pesa ~10 veces
    // menos. Los logos con transparencia son livianos y no llegan a este paso.
    () => redimensionarConCanvas(archivo, true),
  ];
  let mejor = archivo;
  for (const intento of intentos) {
    try {
      const resultado = await intento(); // eslint-disable-line no-await-in-loop
      if (resultado && resultado.size < mejor.size) mejor = resultado;
      if (esChica(mejor)) return mejor;
    } catch (e) {
      console.warn('[fotos] falló un intento de compresión:', e?.message || e);
    }
  }
  if (mejor === archivo) console.warn('[fotos] no se pudo comprimir; se sube el original', archivo.size);
  return mejor;
}

export async function subirFoto(archivo) {
  const comprimido = await comprimirFoto(archivo);
  // Carpeta del negocio: las políticas de Storage solo dejan cambiar/borrar
  // fotos dentro de la carpeta de tu propio negocio.
  const limpio = (archivo.name || 'foto').replace(/[^a-zA-Z0-9._-]/g, '_').slice(-80);
  const nombre = `${getNegocioId()}/${Date.now()}-${Math.random().toString(36).substring(7)}-${limpio}`;
  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(nombre, comprimido, { contentType: comprimido.type || archivo.type || 'image/jpeg' });
  if (error) throw error;
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(nombre);
  return data.publicUrl;
}

// Sube el logo del negocio (reusa el mismo bucket y compresión).
export async function subirLogo(archivo) {
  return subirFoto(archivo);
}

// Borra de Storage todas las fotos de un producto (limpieza de huérfanas).
// Es best-effort: si falla, el llamador decide si es crítico.
export async function eliminarFotosDeProducto(producto) {
  const marcador = `/object/public/${BUCKET}/`;
  const nombres = [1, 2, 3, 4, 5]
    .map((num) => producto[`foto_${num}_url`])
    .filter((url) => url && url.includes(marcador))
    .map((url) => decodeURIComponent(url.split(marcador)[1]));
  if (nombres.length === 0) return;
  const { error } = await supabase.storage.from(BUCKET).remove(nombres);
  if (error) throw error;
}

// Sube en paralelo las fotos presentes en un objeto { foto_1, foto_2, foto_3 }.
// Devuelve { foto_1_url, ... } solo con las fotos que se subieron.
export async function subirFotos(fotos) {
  const resultados = await Promise.all(
    [1, 2, 3, 4, 5].map(async (num) => {
      const archivo = fotos[`foto_${num}`];
      if (!archivo) return null;
      const url = await subirFoto(archivo);
      return { campo: `foto_${num}_url`, url };
    })
  );
  const urls = {};
  resultados.forEach((r) => {
    if (r) urls[r.campo] = r.url;
  });
  return urls;
}
